export const STORAGE_KEY = 'wishlist:product-ids';
const HANDLES_KEY = 'wishlist:product-handles';
const iconAnimations = new WeakMap();

function animateWishlistIcon(element) {
  const icon = element.querySelector('svg');
  if (!icon) return;
  iconAnimations.get(icon)?.cancel();
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  iconAnimations.set(icon, icon.animate([
    { opacity: 0, transform: 'translateY(6px)', filter: 'blur(2px)' },
    { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
  ], {
    duration: 280,
    easing: getComputedStyle(icon).getPropertyValue('--ease-smooth').trim() || 'cubic-bezier(0.22, 1, 0.36, 1)',
  }));
}

export function readIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? [...new Set(ids.map(String).filter((id) => /^\d+$/.test(id)))] : [];
  } catch {
    return [];
  }
}

export function readHandles() {
  try {
    const handles = JSON.parse(localStorage.getItem(HANDLES_KEY) || '{}');
    return handles && typeof handles === 'object' && !Array.isArray(handles) ? handles : {};
  } catch { return {}; }
}

function writeIds(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private browsing, quota, etc.) - fail silently.
  }
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.productId = this.getAttribute('product-id');
    this.button = this.querySelector('button');

    if (!this.productId || !this.button) return;

    this.listeners?.abort();
    this.listeners = new AbortController();
    this.button.addEventListener('click', () => this.toggle(), { signal: this.listeners.signal });
    document.addEventListener('wishlist:change', () => this.render(), { signal: this.listeners.signal });
    this.onStorage = (event) => {
      if (event.key === STORAGE_KEY) this.render();
    };
    window.addEventListener('storage', this.onStorage);

    this.render();
    if (this.isSaved()) this.rememberHandle();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    if (this.onStorage) window.removeEventListener('storage', this.onStorage);
  }

  isSaved() {
    return readIds().includes(this.productId);
  }

  toggle() {
    const ids = readIds();
    const index = ids.indexOf(this.productId);
    const saved = index === -1;

    if (saved) {
      ids.push(this.productId);
      this.rememberHandle();
    } else {
      ids.splice(index, 1);
    }

    writeIds(ids);
    this.render();
    animateWishlistIcon(this.button);

    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        bubbles: true,
        detail: { productId: this.productId, saved },
      })
    );
  }

  rememberHandle() {
    const handle = this.getAttribute('product-handle');
    if (!handle) return;
    try { localStorage.setItem(HANDLES_KEY, JSON.stringify({ ...readHandles(), [this.productId]: handle })); } catch {}
  }

  render() {
    const saved = this.isSaved();
    const label = saved ? this.getAttribute('remove-label') : this.getAttribute('add-label');

    this.classList.toggle('is-active', saved);
    this.button.setAttribute('aria-pressed', String(saved));

    if (label) this.button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.bubble = this.querySelector('.count-bubble');

    if (!this.bubble) return;

    this.onClick = () => animateWishlistIcon(this);
    this.addEventListener('click', this.onClick);

    this.onStorage = (event) => {
      if (event.key === STORAGE_KEY) this.render();
    };
    this.onChange = () => this.render();

    window.addEventListener('storage', this.onStorage);
    document.addEventListener('wishlist:change', this.onChange);

    this.render();
  }

  disconnectedCallback() {
    if (this.onClick) this.removeEventListener('click', this.onClick);
    if (this.onStorage) window.removeEventListener('storage', this.onStorage);
    if (this.onChange) document.removeEventListener('wishlist:change', this.onChange);
  }

  render() {
    const count = readIds().length;

    this.bubble.textContent = String(count);
    this.bubble.classList.toggle('opacity-0', count === 0);
  }
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
